import { useEffect, useState } from 'react';
import { ApiError, del, get, patch, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { BulkEditSheet, PickAll, type BulkField, type BulkValues } from '../ui/bulk';
import { useSession } from '../lib/session';
import { useCart } from '../lib/cart';
import { usePlan } from '../lib/plan';
import { formatCents } from '../../shared/money';
import { formatDateTime, formatPhone, normalizePhone } from '../../shared/format';
import { Badge, Button, Confirm, Empty, Field, Money, MoneyInput, Sheet, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { StatusPill } from './Repairs';
import { QrCode } from '../ui/qr';
import { usePrint } from '../ui/print';
import { CustomerPicker } from './CustomerPicker';

/** Special order statuses at a glance (the same colors as the Orders screen). */
const ORDER_TONE: Record<string, 'blue' | 'amber' | 'green' | 'plain' | 'red'> = { ordered: 'amber', shipped: 'blue', arrived: 'green', picked_up: 'plain', cancelled: 'red' };

/** A purchase that was a bill payment or an activation says so (with the carrier). */
function saleTitle(s: any): string {
  const kinds: string[] = s.carrier_kinds ?? [];
  const what = [kinds.includes('billpay') ? 'Bill payment' : null, kinds.includes('activation') ? 'Activation' : null].filter(Boolean).join(' and ');
  if (!what) return `Purchase · receipt #${s.receipt_no}`;
  return `${what}${s.carriers?.length ? ` · ${s.carriers.join(', ')}` : ''} · receipt #${s.receipt_no}`;
}

/** "a, b and c" */
function andList(items: string[]): string {
  return items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** What a customer still has that deleting would lose (the server checks every store too). */
function stillHas(x: any): string[] {
  const left: string[] = [];
  if (x.store_credit_cents > 0) left.push(`${formatCents(x.store_credit_cents)} store credit`);
  if (x.points_balance > 0) left.push(`${Number(x.points_balance).toLocaleString('en-US')} points`);
  const repairs = (x.repairs ?? []).filter((r: any) => ['checked_in', 'in_progress', 'waiting_parts', 'waiting_customer', 'ready'].includes(r.status));
  if (repairs.length) left.push(`${repairs.length} open repair${repairs.length === 1 ? '' : 's'} (${repairs.map((r: any) => r.ticket).join(', ')})`);
  const orders = (x.orders ?? []).filter((o: any) => o.open);
  if (orders.length) left.push(`${orders.length} open special order${orders.length === 1 ? '' : 's'} (${orders.map((o: any) => o.number).join(', ')})`);
  return left;
}

export function CustomersScreen() {
  const { can } = useSession();
  const [q, setQ] = useState('');
  const dq = useDebounced(q, 250);
  const list = useLoad(() => get(`/api/customers${qs({ q: dq, limit: 50 })}`), [dq]);
  const [adding, setAdding] = useState(false);
  // Tick customers (or Select all) and change their texting, deals or notes in one go.
  const [picked, setPicked] = useState<string[]>([]);
  const [editingMany, setEditingMany] = useState(false);
  const rows: any[] = list.data ?? [];
  const chosen = rows.filter((c) => picked.includes(c.id));
  const allPicked = rows.length > 0 && chosen.length === rows.length;
  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const editing = can('customer.edit');
  const bulkFields: BulkField[] = [
    { key: 'smsOptIn', label: 'Texts about their repairs and orders', kind: 'yesno', yes: 'Agreed', no: 'Not agreed' },
    { key: 'marketingOptIn', label: 'Deals by text', kind: 'yesno', yes: 'Agreed', no: 'Not agreed' },
    { key: 'emailOptIn', label: 'Emails', kind: 'yesno', yes: 'Agreed', no: 'Not agreed' },
    { key: 'notes', label: 'Note', kind: 'text', hint: 'blank: unchanged · replaces the note on each', maxLength: 500 },
  ];
  const applyBulk = async (item: { id: string }, v: BulkValues) => {
    const body: Record<string, unknown> = {};
    for (const k of ['smsOptIn', 'marketingOptIn', 'emailOptIn'] as const) if (typeof v[k] === 'boolean') body[k] = v[k];
    if (typeof v.notes === 'string') body.notes = v.notes.trim() || null;
    await patch(`/api/customers/${item.id}`, body);
  };
  return (
    <div className="page">
      <div className="page-head">
        <h1>Customers</h1>
        {can('customer.edit') ? <Button variant="primary" icon="plus" onClick={() => setAdding(true)}>New customer</Button> : null}
      </div>
      <div className="search">
        <Icon.search />
        <input className="input input-lg" placeholder="Phone number, name or email" value={q} onChange={(e) => setQ(e.target.value)} autoFocus aria-label="Search customers" />
      </div>
      {list.loading && !list.data ? <Spinner /> : null}
      {editing && rows.length ? (
        <div className="pick-bar" role="region" aria-label="Pick customers">
          <PickAll all={allPicked} picked={chosen.length} total={rows.length} what="customers" onToggle={() => setPicked(allPicked ? [] : rows.map((c) => c.id))} />
          <span className="grow" />
          {chosen.length ? (
            <>
              <Button size="sm" variant="ghost" onClick={() => setPicked([])}>Clear</Button>
              <Button size="sm" icon="edit" onClick={() => setEditingMany(true)}>Edit {chosen.length}</Button>
            </>
          ) : <span className="small muted">Tick customers to change their texting, deals or note together</span>}
        </div>
      ) : null}
      {list.data ? (
        list.data.length ? (
          <div className="list">
            {list.data.map((c: any) => (
              <div key={c.id} className="pc-row">
                {editing ? <label className="pc-pick" aria-label={`Pick ${c.name}`}><input type="checkbox" checked={picked.includes(c.id)} onChange={() => toggle(c.id)} /></label> : null}
                <a className="list-row" href={`#/customers/${c.id}`}>
                  <span className="avatar avatar-sm">{c.name.slice(0, 1)}</span>
                  <span className="main-col"><span className="t" style={{ display: 'block' }}>{c.name}</span><span className="s" style={{ display: 'block' }}>{[formatPhone(c.phone), c.email].filter(Boolean).join(' · ') || 'No contact info'}</span></span>
                  {c.store_credit_cents ? <Badge tone="green">Credit {formatCents(c.store_credit_cents)}</Badge> : null}
                  <Icon.chevronRight />
                </a>
              </div>
            ))}
          </div>
        ) : (
          <Empty icon="users" title={q ? 'No customers match.' : 'No customers yet.'} />
        )
      ) : null}
      {adding ? <CustomerPicker onClose={() => setAdding(false)} onPick={(c) => { setAdding(false); navigate(`/customers/${c.id}`); }} /> : null}
      {editingMany ? <BulkEditSheet title="Edit customers" what="customers" items={chosen.map((c) => ({ id: c.id, label: `${c.name}${c.phone ? ` · ${formatPhone(c.phone)}` : ''}` }))} fields={bulkFields} apply={applyBulk} onClose={() => setEditingMany(false)} onDone={() => { setEditingMany(false); setPicked([]); list.reload(); }} /> : null}
    </div>
  );
}

export function CustomerProfileScreen({ id }: { id: string }) {
  const { can, me } = useSession();
  // Texting and deals by text run on West Coast Wireless's own number: its store only, for now.
  const texting = usePlan().home;
  const cart = useCart();
  const toast = useToast();
  const c = useLoad(() => get(`/api/customers/${id}`), [id]);
  const [editing, setEditing] = useState(false);
  const [credit, setCredit] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [merging, setMerging] = useState(false);
  const { run, busy } = useAction();
  // Only this customer's data (not the last one's, while another loads or can't be found).
  const data = c.data && c.data.id === id ? c.data : null;
  if (c.loading && !data) return <Spinner />;
  if (!data) {
    // An old link to a duplicate that was merged: go to the customer it went into.
    const merged = c.error instanceof ApiError && c.error.code === 'merged' ? c.error.details : null;
    return merged ? (
      <Empty icon="users" title="This customer was merged.">
        <p>It was a duplicate. Everything is on <a href={`#/customers/${merged.customerId}`}>{merged.name}</a> now.</p>
      </Empty>
    ) : <Empty icon="users" title="Customer not found." />;
  }
  const x = data;
  const st = x.stats;
  const left = stillHas(x);
  const startSale = () => {
    cart.setCustomer({ id: x.id, name: x.name, phone: x.phone, email: x.email, store_credit_cents: x.store_credit_cents, points_balance: x.points_balance, member_code: x.member_code });
    toast(`${x.name} added to the sale.`, 'good');
    navigate('/sale');
  };
  const remove = async () => {
    const r = await run(() => del(`/api/customers/${id}`), { success: 'Customer deleted.' });
    if (r) navigate('/customers');
  };
  const history = [
    ...x.sales.map((s: any) => ({ at: s.created_at, el: (
      <a key={s.id} className="list-row" href={`#/sales/${s.id}`}>
        {s.carrier_kinds?.includes('activation') ? <Icon.sim /> : s.carrier_kinds?.length ? <Icon.bill /> : <Icon.bag />}
        <span className="main-col"><span className="t" style={{ display: 'block' }}>{saleTitle(s)}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(s.created_at)} · {s.location_name} · {s.items}</span></span>
        <span className="right"><Money cents={s.total_cents} className="strong" />{s.refunded_cents ? <span className="small muted" style={{ display: 'block' }}>refunded {formatCents(s.refunded_cents)}</span> : null}</span>
      </a>
    ) })),
    ...(x.orders ?? []).map((o: any) => ({ at: o.created_at, el: (
      <a key={o.id} className="list-row" href={`#/orders/${o.id}`}>
        <Icon.truck />
        <span className="main-col"><span className="t" style={{ display: 'block' }}>Special order · {o.number}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(o.created_at)} · {o.location_name} · {o.description}{o.open ? (o.paid ? ' · paid' : ' · pays at pickup') : ''}</span></span>
        <span className="right"><Badge tone={ORDER_TONE[o.status] ?? 'plain'}>{o.status_label}</Badge><span className="small muted" style={{ display: 'block', textAlign: 'right' }}>{formatCents(o.price_cents)}</span></span>
      </a>
    ) })),
    ...(x.buybacks ?? []).map((bb: any) => ({ at: bb.created_at, el: (
      <a key={bb.id} className="list-row" href={`#/buybacks/${bb.id}`}>
        <Icon.phoneCash />
        <span className="main-col"><span className="t" style={{ display: 'block' }}>{bb.kind === 'trade_in' ? 'Trade-in' : 'Sold us a phone'} · {bb.number}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(bb.created_at)} · {bb.location_name} · {bb.brand} {bb.model}{bb.storage ? ` ${bb.storage}` : ''}{bb.status === 'voided' ? ' · voided' : ''}</span></span>
        <span className="right"><Money cents={bb.offer_cents} className="strong" /></span>
      </a>
    ) })),
    ...(x.repairs ?? []).map((rp: any) => ({ at: rp.created_at, el: (
      <a key={rp.id} className="list-row" href={`#/repairs/${rp.id}`}>
        <Icon.wrench />
        <span className="main-col"><span className="t" style={{ display: 'block' }}>Repair · {rp.ticket}</span><span className="s" style={{ display: 'block' }}>{formatDateTime(rp.created_at)} · {rp.brand} {rp.model} · {rp.services || 'not priced yet'}</span></span>
        <span className="right"><StatusPill status={rp.status} /><span className="small muted" style={{ display: 'block', textAlign: 'right' }}>{formatCents(rp.price_cents)}</span></span>
      </a>
    ) })),
  ].sort((a, b) => b.at - a.at);
  return (
    <div className="page">
      <div className="page-head">
        <span className="avatar">{x.name.slice(0, 1)}</span>
        <div style={{ marginRight: 'auto' }}>
          <h1>{x.name}</h1>
          <p className="muted">{[formatPhone(x.phone), x.email].filter(Boolean).join(' · ') || 'No contact info'}</p>
        </div>
        {me?.register ? <Button variant="primary" icon="bag" onClick={startSale}>New sale</Button> : null}
        {can('repair.create') ? <Button icon="wrench" onClick={() => navigate(`/repairs/new?customer=${x.id}`)}>New repair</Button> : null}
        {can('order.create') ? <Button icon="truck" onClick={() => navigate(`/orders/new?customer=${x.id}`)}>Special order</Button> : null}
        {can('customer.edit') ? <Button icon="edit" onClick={() => setEditing(true)}>Edit</Button> : null}
      </div>
      <div className="stat-row">
        <div className="stat"><div className="k">Purchases</div><div className="v">{st.purchases}</div></div>
        <div className="stat"><div className="k">Phones bought</div><div className="v">{st.phones_purchased}</div></div>
        <div className="stat"><div className="k">Repairs</div><div className="v">{st.repairs}</div></div>
        <div className="stat"><div className="k">Special orders</div><div className="v">{st.special_orders ?? 0}</div></div>
        <div className="stat"><div className="k">Trade-ins</div><div className="v">{st.trade_ins}</div></div>
        <div className="stat"><div className="k">Buybacks</div><div className="v">{st.buybacks}</div></div>
        {st.activations ? <div className="stat"><div className="k">Activations</div><div className="v">{st.activations}</div></div> : null}
        {st.bill_payments ? <div className="stat"><div className="k">Bill payments</div><div className="v">{st.bill_payments}</div></div> : null}
        <div className="stat"><div className="k">Lifetime spend</div><div className="v">{formatCents(st.lifetime_spend_cents)}</div></div>
        <div className="stat"><div className="k">Store credit</div><div className="v">{formatCents(x.store_credit_cents)}</div></div>
        <div className="stat"><div className="k">Points</div><div className="v">{Number(x.points_balance ?? 0).toLocaleString('en-US')}</div></div>
      </div>
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="stack">
          <h2>History</h2>
          {history.length ? <div className="list">{history.map((h) => h.el)}</div> : <Empty icon="history" title="Nothing yet." />}
        </section>
        <section className="stack">
          <PointsCard customer={x} onChanged={c.reload} />
          <div className="card stack-sm">
            <h3>Details</h3>
            <dl className="kv">
              <dt>Address</dt><dd>{[x.address1, x.city, x.state, x.zip].filter(Boolean).join(', ') || '—'}</dd>
              {texting ? <><dt>Texts</dt><dd>{x.sms_opt_in ? `Agreed to texts${x.sms_consent_at ? ` (${x.sms_consent_source ?? 'agreed'}, ${formatDateTime(x.sms_consent_at)})` : ''}` : 'No texts'}{x.phone && can('messages.use') ? <> · <a href={`#/messages?customer=${x.id}`}>Text them</a></> : null}</dd></> : null}
              <dt>Emails</dt><dd>{x.email_opt_in ? 'Agreed to emails' : 'No emails'}</dd>
              {texting ? <><dt>Deals & news</dt><dd>{x.marketing_opt_in ? 'Yes, by text and email' : 'No'}</dd></> : null}
              <dt>Customer since</dt><dd>{formatDateTime(x.created_at)}</dd>
              {x.notes ? <><dt>Notes</dt><dd style={{ whiteSpace: 'pre-line' }}>{x.notes}</dd></> : null}
            </dl>
          </div>
          <div className="card stack-sm">
            <div className="spread"><h3>Store credit</h3>{can('customer.edit') ? <Button size="sm" onClick={() => setCredit(true)}>Add or remove</Button> : null}</div>
            {x.store_credit.length ? x.store_credit.map((l: any) => (
              <div key={l.id} className="spread small"><span>{formatDateTime(l.created_at)} · {l.reason}{l.employee_name ? ` · ${l.employee_name}` : ''}</span><span className="strong num">{l.amount_cents > 0 ? '+' : ''}{formatCents(l.amount_cents)}</span></div>
            )) : <p className="small muted">No store credit history.</p>}
          </div>
          <div className="row-wrap">
            {can('customer.edit') ? <Button icon="users" onClick={() => setMerging(true)}>Merge with…</Button> : null}
            {can('customer.delete') ? <Button variant="danger" icon="trash" onClick={() => setDeleting(true)}>Delete customer</Button> : null}
          </div>
        </section>
      </div>
      {editing ? <EditCustomer customer={x} onClose={() => setEditing(false)} onDone={() => { setEditing(false); c.reload(); }} /> : null}
      {credit ? <CreditSheet customer={x} onClose={() => setCredit(false)} onDone={() => { setCredit(false); c.reload(); }} /> : null}
      {merging ? <MergeSheet customer={x} onClose={() => setMerging(false)} onDone={(keptId) => { setMerging(false); if (keptId === x.id) c.reload(); else navigate(`/customers/${keptId}`); }} /> : null}
      {deleting ? (
        left.length ? (
          <Sheet title="This customer can’t be deleted yet" onClose={() => setDeleting(false)} footer={<><Button onClick={() => setDeleting(false)}>OK</Button>{can('customer.edit') ? <Button variant="primary" icon="users" onClick={() => { setDeleting(false); setMerging(true); }}>Merge with…</Button> : null}</>}>
            <p>{x.name} still has {andList(left)}.</p>
            <p className="small muted">Use those up or finish them first. If this is a duplicate, merge it with the right customer instead: everything moves over.</p>
          </Sheet>
        ) : (
          <Confirm title="Delete this customer?" confirmLabel="Delete" danger busy={busy} onClose={() => setDeleting(false)} onConfirm={remove}>Their past receipts stay in the system. This is recorded in the audit log.</Confirm>
        )
      ) : null}
    </div>
  );
}

const MOVE_WORDS: [string, string, string][] = [
  ['sales', 'sale', 'sales'], ['repairs', 'repair', 'repairs'], ['orders', 'special order', 'special orders'], ['buybacks', 'buy or trade-in', 'buys and trade-ins'],
  ['conversations', 'text conversation', 'text conversations'], ['calls', 'call', 'calls'], ['appointments', 'visit', 'visits'],
];

/**
 * "Merge with…": pick the duplicate, see what moves, and merge. Everything moves onto the
 * customer being kept, and the duplicate is removed. A manager's PIN when the role can't delete customers.
 */
function MergeSheet({ customer, onClose, onDone }: { customer: any; onClose: () => void; onDone: (keptId: string) => void }) {
  const toast = useToast();
  const { run, busy } = useAction();
  // Duplicates rarely match in full ("Maria L" and "Maria Lopez"): start from the first name.
  const [q, setQ] = useState(customer.first_name || customer.last_name || '');
  const dq = useDebounced(q, 200);
  const hits = useLoad(() => get(`/api/customers${qs({ q: dq, limit: 12 })}`), [dq]);
  const [other, setOther] = useState<any>(null);
  // Which one stays: this customer, unless staff pick the other one to keep.
  const [keepThis, setKeepThis] = useState(true);
  const keep = keepThis ? customer : other;
  const dup = keepThis ? other : customer;
  const [preview, setPreview] = useState<any>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    setPreview(null);
    setProblem(null);
    if (!keep || !dup) return;
    let live = true;
    get(`/api/customers/${keep.id}/merge${qs({ other: dup.id })}`).then((p) => live && setPreview(p)).catch((e) => live && setProblem(e instanceof Error ? e.message : 'Could not load.'));
    return () => { live = false; };
  }, [keep?.id, dup?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const merge = async () => {
    const r = await run((approval?: ApprovalInput) => post(`/api/customers/${keep.id}/merge`, { duplicateId: dup.id, approval }));
    if (!r) return;
    toast(`Merged. Everything from ${dup.name} is on ${keep.name} now.`, 'good');
    onDone(keep.id);
  };
  const list: any[] = (hits.data ?? []).filter((h: any) => h.id !== customer.id);
  if (!other) {
    return (
      <Sheet title="Merge with…" onClose={onClose}>
        <p className="small muted">Pick the duplicate of {customer.name}. Everything moves onto {customer.name} and the duplicate is removed. You see what moves before anything changes.</p>
        <div className="search">
          <Icon.search />
          <input className="input input-lg" autoFocus placeholder="Phone number, name or email" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search for the duplicate" />
        </div>
        <div className="list">
          {list.length ? list.map((h) => (
            <button key={h.id} type="button" className="list-row" onClick={() => setOther(h)}>
              <span className="avatar avatar-sm">{h.name.slice(0, 1)}</span>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{h.name}</span>
                <span className="s" style={{ display: 'block' }}>{[formatPhone(h.phone), h.email].filter(Boolean).join(' · ') || 'No contact info'}</span>
              </span>
              {h.points_balance ? <Badge>{Number(h.points_balance).toLocaleString('en-US')} pts</Badge> : null}
              {h.store_credit_cents ? <Badge tone="green">Credit {formatCents(h.store_credit_cents)}</Badge> : null}
            </button>
          )) : <div className="empty">{hits.loading ? 'Searching…' : 'No other customer matches.'}</div>}
        </div>
      </Sheet>
    );
  }
  const moves = preview ? MOVE_WORDS.map(([k, one, many]) => (preview.moves[k] ? `${preview.moves[k]} ${preview.moves[k] === 1 ? one : many}` : null)).filter(Boolean) as string[] : [];
  const card = (c: any, label: string, tone: 'green' | 'red') => (
    <div className="card card-tight stack-sm merge-side">
      <Badge tone={tone}>{label}</Badge>
      <div className="strong">{c.name}</div>
      <div className="small muted">{[formatPhone(c.phone), c.email].filter(Boolean).join(' · ') || 'No contact info'}</div>
      {c.member_code ? <div className="tiny muted">Member {c.member_code}</div> : null}
    </div>
  );
  return (
    <Sheet
      title="Merge these customers?"
      onClose={onClose}
      wide
      footer={<><Button onClick={() => { setOther(null); setKeepThis(true); }}>Back</Button><Button variant="primary" busy={busy} disabled={!preview} onClick={merge}>Merge</Button></>}
    >
      <div className="grid-2">
        {card(keep, 'Keep', 'green')}
        {card(dup, 'Remove (duplicate)', 'red')}
      </div>
      <button type="button" className="link-btn small" onClick={() => setKeepThis(!keepThis)}>Keep {dup.name} instead</button>
      {problem ? <div className="banner bad"><Icon.alert /><div>{problem}</div></div> : !preview ? <Spinner /> : (
        <div className="stack-sm">
          <p>Moves onto {keep.name}: {moves.length ? andList(moves) : 'no sales, repairs or orders'}.</p>
          {preview.store_credit_cents || preview.points ? (
            <p>Added together: {[preview.store_credit_cents ? `${formatCents(preview.store_credit_cents)} store credit` : null, preview.points ? `${Number(preview.points).toLocaleString('en-US')} points` : null].filter(Boolean).join(' and ')}.</p>
          ) : null}
          {preview.fills.length ? <p>Filled in from the duplicate: {preview.fills.join(', ')}.</p> : null}
          <p className="small muted">{keep.name}’s member code stays; {dup.name}’s card stops working. This can’t be undone, and it’s recorded in the audit log.</p>
        </div>
      )}
    </Sheet>
  );
}

function EditCustomer({ customer, onClose, onDone }: { customer: any; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const texting = usePlan().home;
  const [f, setF] = useState({
    firstName: customer.first_name, lastName: customer.last_name, phone: formatPhone(customer.phone), email: customer.email ?? '',
    address1: customer.address1 ?? '', city: customer.city ?? '', state: customer.state ?? 'CA', zip: customer.zip ?? '', notes: customer.notes ?? '',
    smsOptIn: !!customer.sms_opt_in, emailOptIn: !!customer.email_opt_in, marketingOptIn: !!customer.marketing_opt_in,
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  const phoneOk = !f.phone || !!normalizePhone(f.phone);
  const save = async () => {
    const r = await run(() => patch(`/api/customers/${customer.id}`, { ...f, phone: f.phone || null, email: f.email || null }), { success: 'Saved.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Edit customer" onClose={onClose} wide footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!phoneOk} onClick={save}>Save</Button></>}>
      <div className="form-grid">
        <Field label="First name"><input className="input" value={f.firstName} onChange={set('firstName')} /></Field>
        <Field label="Last name"><input className="input" value={f.lastName} onChange={set('lastName')} /></Field>
        <Field label="Mobile number"><input className="input" inputMode="tel" value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Email"><input className="input" type="email" value={f.email} onChange={set('email')} /></Field>
        <Field label="Street address" className="full"><input className="input" value={f.address1} onChange={set('address1')} /></Field>
        <Field label="City"><input className="input" value={f.city} onChange={set('city')} /></Field>
        <div className="grid-2">
          <Field label="State"><input className="input" value={f.state} onChange={set('state')} maxLength={2} /></Field>
          <Field label="ZIP"><input className="input" inputMode="numeric" value={f.zip} onChange={set('zip')} /></Field>
        </div>
        <Field label="Notes" className="full"><textarea className="textarea" value={f.notes} onChange={set('notes')} /></Field>
        {texting ? <label className="check"><input type="checkbox" checked={f.smsOptIn} onChange={(e) => setF({ ...f, smsOptIn: e.target.checked })} /> Agrees to texts</label> : null}
        <label className="check"><input type="checkbox" checked={f.emailOptIn} onChange={(e) => setF({ ...f, emailOptIn: e.target.checked })} /> Agrees to emails</label>
        {texting ? <label className="check full"><input type="checkbox" checked={f.marketingOptIn} onChange={(e) => setF({ ...f, marketingOptIn: e.target.checked })} /> Wants deals and rewards news by text and email (ask them)</label> : null}
      </div>
      {!phoneOk ? <div className="error-text">Mobile numbers need 10 digits.</div> : null}
    </Sheet>
  );
}

function CreditSheet({ customer, onClose, onDone }: { customer: any; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [sign, setSign] = useState<1 | -1>(1);
  const [amount, setAmount] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const save = async () => {
    const r = await run((approval?: ApprovalInput) => post(`/api/customers/${customer.id}/store-credit`, { amountCents: sign * (amount ?? 0), reason, approval }), { success: 'Store credit updated.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Store credit" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!amount || !reason.trim()} onClick={save}>Save</Button></>}>
      <p className="muted">{customer.name} has {formatCents(customer.store_credit_cents)}.</p>
      <div className="chips">
        <button type="button" className="chip" aria-pressed={sign === 1} onClick={() => setSign(1)}>Add credit</button>
        <button type="button" className="chip" aria-pressed={sign === -1} onClick={() => setSign(-1)}>Remove credit</button>
      </div>
      <Field label="Amount"><MoneyInput value={amount} onChange={setAmount} autoFocus /></Field>
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Goodwill for a late repair" /></Field>
      <p className="small muted">A manager PIN may be needed. Every change is logged.</p>
    </Sheet>
  );
}

const REASONS: Record<string, string> = {
  earn: 'Earned', redeem: 'Used', refund_earn: 'Taken back (refund)', refund_redeem: 'Given back (refund)',
  online_earn: 'Earned online', online_refund: 'Taken back (online refund)', adjust: 'Changed by a manager',
  merge_in: 'Merged from a duplicate', merge_out: 'Moved (merged)',
};

/** Points balance, the member QR, history, and manager tools. */
function PointsCard({ customer, onChanged }: { customer: any; onChanged: () => void }) {
  const { can } = useSession();
  const print = usePrint();
  const pts = useLoad(() => get(`/api/customers/${customer.id}/points`), [customer.id, customer.points_balance]);
  const [sheet, setSheet] = useState<null | 'adjust' | 'code'>(null);
  const [all, setAll] = useState(false);
  const p = pts.data;
  if (!p) return <div className="card"><Spinner label="Loading points" /></div>;
  const rows = all ? p.history : p.history.slice(0, 6);
  return (
    <div className="card stack-sm">
      <div className="spread"><h3>Points</h3>{p.linked_to_shopify ? <Badge tone="green" icon="check">Linked to the website</Badge> : null}</div>
      <div className="points-head">
        {p.member_code ? <QrCode text={p.member_code} size={96} label={`Member code ${p.member_code}`} /> : null}
        <div>
          <div className="big num">{p.balance.toLocaleString('en-US')}</div>
          <div className="small muted">{p.balance > 0 ? `Worth ${formatCents(p.worth_cents)} off` : p.balance < 0 ? 'Below zero after a refund. Can’t use points yet.' : 'No points yet'}</div>
          <div className="small strong" style={{ marginTop: 4 }}>{p.member_code ?? '—'}</div>
        </div>
      </div>
      <div className="row-wrap">
        {p.member_code ? <Button size="sm" icon="printer" onClick={() => print(<MemberCardPaper name={customer.name} code={p.member_code} balance={p.balance} />, 'thermal')}>Print member card</Button> : null}
        {can('points.adjust') ? <Button size="sm" onClick={() => setSheet('adjust')}>Add or remove</Button> : null}
        {can('points.adjust') ? <Button size="sm" variant="ghost" onClick={() => setSheet('code')}>Replace code</Button> : null}
      </div>
      {rows.length ? rows.map((h: any) => (
        <div key={h.id} className="spread small">
          <span>{formatDateTime(h.created_at)} · {REASONS[h.reason] ?? h.reason}{h.note ? ` · ${h.note}` : ''}</span>
          <span className={`strong num ${h.points < 0 ? 'neg' : ''}`}>{h.points > 0 ? '+' : ''}{h.points.toLocaleString('en-US')}</span>
        </div>
      )) : <p className="small muted">Earns {p.settings.perDollar} point{p.settings.perDollar === 1 ? '' : 's'} per $1. {p.settings.minRedeem} points = {formatCents(p.settings.minRedeem * p.settings.centsPerPoint)} off.</p>}
      {p.history.length > 6 ? <Button size="sm" variant="ghost" onClick={() => setAll(!all)}>{all ? 'Show less' : `Show all ${p.history.length}`}</Button> : null}
      {sheet === 'adjust' ? <AdjustPointsSheet customer={customer} balance={p.balance} onClose={() => setSheet(null)} onDone={() => { setSheet(null); pts.reload(); onChanged(); }} /> : null}
      {sheet === 'code' ? <ReplaceCodeSheet customer={customer} onClose={() => setSheet(null)} onDone={() => { setSheet(null); pts.reload(); onChanged(); }} /> : null}
    </div>
  );
}

function AdjustPointsSheet({ customer, balance, onClose, onDone }: { customer: any; balance: number; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [sign, setSign] = useState<1 | -1>(1);
  const [n, setN] = useState('');
  const [reason, setReason] = useState('');
  const count = Math.floor(Number(n) || 0);
  const save = async () => {
    const r = await run(() => post(`/api/customers/${customer.id}/points`, { points: sign * count, reason }), { success: 'Points updated.' });
    if (r) onDone();
  };
  return (
    <Sheet title="Add or remove points" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" busy={busy} disabled={!count || !reason.trim()} onClick={save}>Save</Button></>}>
      <p className="muted">{customer.name} has {balance.toLocaleString('en-US')} points.</p>
      <div className="chips">
        <button type="button" className="chip" aria-pressed={sign === 1} onClick={() => setSign(1)}>Add points</button>
        <button type="button" className="chip" aria-pressed={sign === -1} onClick={() => setSign(-1)}>Remove points</button>
      </div>
      <Field label="Points"><input className="input input-lg" inputMode="numeric" autoFocus value={n} onChange={(e) => setN(e.target.value.replace(/\D/g, ''))} /></Field>
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Points missed on receipt #100231" /></Field>
      <p className="small muted">Every change is recorded with your name in the audit log.</p>
    </Sheet>
  );
}

function ReplaceCodeSheet({ customer, onClose, onDone }: { customer: any; onClose: () => void; onDone: () => void }) {
  const { run, busy } = useAction();
  const [reason, setReason] = useState('Lost card');
  const save = async () => {
    const r = await run(() => post(`/api/customers/${customer.id}/member-code`, { reason }), { success: 'New member code made.' });
    if (r) onDone();
  };
  return (
    <Confirm title="Replace the member code?" confirmLabel="Replace code" busy={busy} onClose={onClose} onConfirm={save}>
      <p>The old card or QR stops working right away. Their points stay the same.</p>
      <Field label="Reason"><input className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Confirm>
  );
}

/** A small card for the thermal printer: the member QR to scan at checkout. */
export function MemberCardPaper({ name, code, balance }: { name: string; code: string; balance: number }) {
  const { me } = useSession();
  return (
    <div className="receipt member-card">
      <div className="c strong">{me?.settings?.business_name ?? 'West Coast Wireless'}</div>
      <div className="c">REWARDS MEMBER</div>
      <hr />
      <div className="c strong">{name}</div>
      <div className="qrwrap"><QrCode text={code} size={150} label={`Member code ${code}`} /><div className="strong">{code}</div></div>
      <div className="c">Points today: {balance.toLocaleString('en-US')}</div>
      <hr />
      <div className="c dim">Show this code at the counter to earn points on every purchase and use them for money off.</div>
    </div>
  );
}
